import { gtmDiscountValue, gtmValue, gtmPriceFormat } from '~/helpers/gtm';
export class GtmService {
  /**
   * Pushes an event.
   *
   * @param      {string}  eventName        The event name
   * @param      {object}  eventProperties  The event properties
   */
  pushEvent(eventName: string, eventProperties): void {
    if (typeof window !== 'undefined' && window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(Object.assign({ event: eventName }, eventProperties));
    } else if (typeof window !== 'undefined') {
      console.error('dataLayer is not defined');
    }
  }

  /**
   * Tracks a menu click
   *
   * @param      {string}  linkName  The link name
   * @param      {string}  target    The target
   */
  trackMenuClick(linkName: string, target: string): void {
    this.pushEvent('menu', {
      event_name: 'menu',
      event_action: 'click',
      link_name: linkName || '',
      page_target: target || '',
    });
  }

  /**
   * Tracks an advice card click
   *
   * @param      {string}  category  The category
   * @param      {string}  title     The title
   * @param      {string}  pageUrl   The page url
   */
  trackAdviceCard(category: string, title: string, pageUrl: string): void {
    this.pushEvent('advice', {
      event: 'generic_event',
      event_category: 'engagement',
      event_action: 'click',
      event_label: 'advice',
      event_name: 'generic_event',
      category,
      advice: title || '',
      page_url: pageUrl || '',
    });
  }

  /**
   * Tracks a login event
   *
   * @param      {User}  user      The user
   * @param      {string}  category  The category (eg. email, google, facebook, etc.)
   * @param      {string}  pageUrl   The page url
   */
  trackLogin(user, category: string, pageUrl: string): void {
    this.pushEvent('login', {
      event_category: 'engagement',
      event_action: 'submit',
      event_label: 'login',
      event_name: 'login',
      origin: pageUrl,
      method: category,
      email: user?.email,
      user_id: user?.id,
    });
  }

  /**
   * Tracks a failed login event
   *
   * @param      {string}  category  The category (eg. email, google, facebook,
   *                                 etc.)
   * @param      {string}  pageUrl   The page url
   */
  trackFailedLogin(category: string, pageUrl: string): void {
    this.pushEvent('login_failed', {
      origin: pageUrl,
      method: category,
    });
  }

  /**
   * Tracks the signup event
   *
   * @param      {User}  user      The user
   */
  trackSignup(user): void {
    this.pushEvent('form', {
      event_category: 'form',
      event_action: 'submit',
      event_label: 'sign_up',
      event_name: 'sign_up',
      origin,
      user_data: {
        firstName: user?.firstName,
        lastName: user?.lastName,
        email: user?.email,
        salutation: user?.salutation,
        dateOfBirth: user?.dateOfBirth,
      },
      newsletter: user?.newsletter,
    });
  }

  /**
   * Tracks an "add_to_cart" event with GTM
   *
   * Supports both single product and multiple products (e.g. recipe bundles).
   *
   * @param {Object} params - The event parameters
   * @param {string} [params.title] - Product name (single item mode only)
   * @param {string} [params.sku] - Product SKU (single item mode only)
   * @param {number|string} [params.originalId] - Optional fallback for SKU
   * @param {number} [params.priceRegular] - Regular price
   * @param {number} [params.priceSpecial] - Discounted price
   * @param {number} [params.quantity=1] - Quantity of product
   * @param {boolean} [params.isNewProduct=false] - Whether the product is new
   * @param {boolean} [params.isMaster=false] - Whether the product is the master variant
   * @param {number} [params.unitPriceExclTaxEur] - Unit price excluding tax in EUR
   * @param {string} [params.itemLocation] - Where the product is located (e.g. 'product_card')
   * @param {number} [params.index] - Index for product listings
   * @param {string} [params.currency] - Currency code (e.g. 'EUR')
   * @param {string} [params.cartId] - Cart ID
   * @param {string} [params.ctId] - Item's Commercetools ID
   * @param {string} [params.pageUrl] - Page URL (defaults to current location)
   * @param {string} [params.sourceId] - Optional source ID for widgets or carousels
   * @param {Record<string, unknown>} [params.extraFields] - Extra fields to include in the item object
   * @param {Array<Record<string, unknown>>} [params.items] - Pre-built list of ecommerce items (multi-item mode)
   */
  trackAddToCart({
    title,
    sku,
    originalId,
    priceRegular,
    priceSpecial,
    quantity = 1,
    isNewProduct = false,
    isMaster = false,
    unitPriceExclTaxEur,
    itemLocation,
    index,
    currency,
    cartId,
    ctId,
    sourceId,
    pageUrl = window.location.pathname,
    extraFields,
    items,
  }: {
    title?: string;
    sku?: string;
    originalId?: string | number;
    priceRegular?: number;
    priceSpecial?: number;
    quantity?: number;
    isNewProduct?: boolean;
    isMaster?: boolean;
    unitPriceExclTaxEur?: number;
    itemLocation?: string;
    index?: number;
    currency?: string;
    cartId?: string;
    ctId?: string;
    sourceId?: string;
    pageUrl?: string;
    extraFields?: Record<string, unknown>;
    items?: Array<Record<string, unknown>>;
  }): void {
    const itemSku = sku || originalId;
    const itemId = `SKU_${itemSku}`;

    /**
     * Builds the `items` array for the GTM ecommerce payload.
     *
     * - If `items` is provided (e.g. in the case of recipes), it is used directly.
     * - Otherwise, constructs a single product object from individual parameters.
     *
     * This supports both single product adds and multi-product adds (like recipes).
     */

    const finalItems = items ?? [
      {
        item_id: itemId,
        item_sku: itemSku,
        item_name: title,
        ...(typeof ctId !== 'undefined' && { ct_product_id: ctId }),
        ...(typeof isNewProduct !== 'undefined' && { new_product: isNewProduct }),
        ...(typeof isMaster !== 'undefined' && { is_master_in_front: isMaster }),
        ...(typeof unitPriceExclTaxEur !== 'undefined' && { unit_price_excl_tax_eur: unitPriceExclTaxEur }),
        ...(typeof priceRegular !== 'undefined' && {
          price: priceRegular,
          price_in_cents: gtmPriceFormat(priceRegular, true),
        }),
        ...(typeof priceRegular !== 'undefined' &&
          typeof priceSpecial !== 'undefined' && {
            discount: gtmDiscountValue(priceRegular, priceSpecial),
            discount_in_cents: gtmDiscountValue(priceRegular, priceSpecial, true),
          }),
        ...(index !== undefined && { index }),
        ...(itemLocation && { item_location: itemLocation }),
        ...(quantity && { quantity }),
        ...(currency && { currency }),
        ...extraFields,
      },
    ];

    const totalQuantity = items
      ? items.reduce((acc, i) => acc + (typeof i.quantity === 'number' ? i.quantity : 1), 0)
      : quantity;

    const totalPriceInCents = items
      ? items.reduce((acc, i) => {
          const price = typeof i.price_in_cents === 'number' ? i.price_in_cents : 0;
          const qty = typeof i.quantity === 'number' ? i.quantity : 1;
          return acc + price * qty;
        }, 0)
      : gtmValue(priceRegular, priceSpecial, true, quantity);

    const totalPrice = totalPriceInCents / 100;

    const payload = {
      event_category: 'ecommerce',
      event_action: 'add',
      event_label: title,
      event_name: 'add_to_cart',
      total_items: totalQuantity,
      cart_id: cartId,
      page_url: pageUrl,
      ...(sourceId && { source_id: sourceId }),
      ecommerce: {
        value: totalPrice,
        value_in_cents: totalPriceInCents,
        items: finalItems,
      },
    };
    this.pushEvent('add_to_cart', payload);
  }
}
